import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Globe, Loader2, Sliders } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input, Select } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { TranslatedInput } from '@/components/TranslatedField'
import {
  getAttributeValues,
  replaceAttributeValues,
} from '@/features/materials/attribute-values-api'
import { subscribeToAttributes } from '@/features/materials/categories-api'
import { CategoryPicker } from '@/features/materials/CategoryPicker'
import { saveMaterial, type MaterialDraft } from '@/features/materials/materials-api'
import {
  clearMaterialPrice,
  getMaterialPrice,
  setMaterialPrice,
} from '@/features/prices/prices-api'
import { describeWriteError } from '@/features/users/permissions'
import { CategoryIndex } from '@/lib/category-ref'
import {
  COST_TYPES,
  COST_TYPE_LABELS,
  CURRENCIES,
  CURRENCY_SYMBOLS,
  TRANSLATION_LANGUAGES,
} from '@/lib/constants'
import type {
  CostType,
  Currency,
  Material,
  MaterialCategory,
  MaterialCategoryAttribute,
  MaterialPrice,
  UnitOfMeasure,
} from '@/lib/types'

/**
 * Creates or edits a catalog item. The base name/description are authored in Russian (that is the
 * base language of shared content); the per-language fields are optional overrides and blank ones
 * are dropped before saving, exactly like `sanitizedTranslations()` in the app.
 */
export function MaterialEditDialog({
  material,
  categories,
  units,
  actor,
  onClose,
}: {
  /** `null` = create a new item. */
  material: Material | null
  categories: MaterialCategory[]
  units: UnitOfMeasure[]
  actor: { uid: string; label: string }
  onClose: () => void
}) {
  const [name, setName] = useState(material?.name ?? '')
  const [code, setCode] = useState(material?.code ?? '')
  // The stored category reference (id + name copy). Resolved against the live catalog below — by id,
  // else by name for items saved before ids existed — so a renamed category stays selected.
  const [categoryRef, setCategoryRef] = useState({
    id: material?.categoryId ?? '',
    name: material?.category ?? '',
  })
  const [unitId, setUnitId] = useState(material?.unitId ?? '')
  const [unitSymbol, setUnitSymbol] = useState(material?.unit ?? '')
  const [costType, setCostType] = useState<CostType>(material?.costType ?? 'MATERIAL')
  const [isActive, setActive] = useState(material?.isActive ?? true)
  const [isPrivate, setPrivate] = useState(material?.isPrivate ?? false)
  const [description, setDescription] = useState(material?.description ?? '')
  const [nameI18n, setNameI18n] = useState<Record<string, string>>(material?.nameI18n ?? {})
  const [descriptionI18n, setDescriptionI18n] = useState<Record<string, string>>(
    material?.descriptionI18n ?? {},
  )
  const [isSaving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Attributes of the selected category, and the values this material provides for them.
  const categoryIndex = useMemo(() => new CategoryIndex(categories), [categories])
  const selectedCategory = categoryIndex.find(categoryRef.id, categoryRef.name)
  const selectedCategoryId = selectedCategory?.id ?? null
  // A reference the catalog does not know: legacy free text, or a deleted category.
  const hasUnknownCategory =
    selectedCategory === null && (categoryRef.id.trim() !== '' || categoryRef.name.trim() !== '')
  const [attributes, setAttributes] = useState<MaterialCategoryAttribute[]>([])
  const [attrValues, setAttrValues] = useState<Record<string, string>>({})

  // Price of this material (its own `prices/{materialId}` document). Lives in the same dialog as in
  // the mobile editor: a price belongs to the material, not to a separate screen.
  const [priceAmount, setPriceAmount] = useState('')
  const [currency, setCurrency] = useState<Currency>('AZN')
  const [storedPrice, setStoredPrice] = useState<MaterialPrice | null>(null)

  // Attribute definitions follow the selected category.
  useEffect(() => {
    if (!selectedCategoryId) {
      setAttributes([])
      return
    }
    return subscribeToAttributes(selectedCategoryId, setAttributes, () => setAttributes([]))
  }, [selectedCategoryId])

  // Stored values are loaded once per material (a one-off read: the dialog is short-lived).
  useEffect(() => {
    if (!material) return
    let cancelled = false
    void getAttributeValues(material.id).then((values) => {
      if (cancelled) return
      setAttrValues(Object.fromEntries(values.map((item) => [item.attributeId, item.value])))
    })
    void getMaterialPrice(material.id).then((price) => {
      if (cancelled || !price) return
      setStoredPrice(price)
      setPriceAmount(String(price.amount))
      setCurrency(price.currency)
    })
    return () => {
      cancelled = true
    }
  }, [material])

  const canSave = name.trim().length > 0 && !isSaving

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    const draft: MaterialDraft = {
      name: name.trim(),
      nameI18n: sanitize(nameI18n),
      code: code.trim(),
      // The id is the link; the name is stored as a copy of the CURRENT name. An unknown reference
      // is kept as it was (free text / a deleted category's id).
      category: (selectedCategory?.name ?? categoryRef.name).trim(),
      categoryId: selectedCategory?.id ?? categoryRef.id.trim(),
      unit: unitSymbol.trim(),
      unitId,
      description: description.trim(),
      descriptionI18n: sanitize(descriptionI18n),
      costType,
      isActive,
      isPrivate,
    }
    try {
      const id = await saveMaterial(material?.id ?? null, draft, actor)
      // Values are replaced as a set: clearing the category clears them, which is what the app does.
      await replaceAttributeValues(
        id,
        selectedCategory
          ? attributes
              .filter((attribute) => (attrValues[attribute.id] ?? '').trim().length > 0)
              .map((attribute) => ({
                categoryId: selectedCategory.id,
                categoryName: selectedCategory.name,
                attributeId: attribute.id,
                attributeCode: attribute.code,
                attributeName: attribute.name,
                value: (attrValues[attribute.id] ?? '').trim(),
                type: attribute.type,
              }))
          : [],
      )

      // The price is its own document, so it is written after the material exists (the rules read the
      // material to decide who may price it). An empty field clears the price; "0" is a real price.
      const trimmedPrice = priceAmount.trim().replace(',', '.')
      if (trimmedPrice.length === 0) {
        if (storedPrice) await clearMaterialPrice(id)
      } else {
        const amount = Number.parseFloat(trimmedPrice)
        if (!Number.isFinite(amount) || amount < 0) {
          setError('Цена должна быть числом не меньше нуля.')
          return
        }
        await setMaterialPrice(
          { id, ownerId: material?.ownerId ?? actor.uid, isPrivate },
          amount,
          currency,
          actor,
        )
      }
      onClose()
    } catch (caught) {
      setError(describeWriteError(caught))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{material ? 'Пункт затрат' : 'Новый пункт затрат'}</DialogTitle>
          <DialogDescription>
            Базовые название и описание пишутся по-русски; переводы — необязательные переопределения.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <label className="grid gap-1.5 text-sm">
            <span className="font-medium">Название (ru)</span>
            <Input value={name} onChange={(event) => setName(event.target.value)} autoFocus />
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-1.5 text-sm">
              <span className="font-medium">Код</span>
              <Input value={code} onChange={(event) => setCode(event.target.value)} />
            </label>

            {/* Not a <label>: a click inside the open list would re-activate the button. */}
            <div className="grid gap-1.5 text-sm">
              <span id="material-category-label" className="font-medium">
                Категория
              </span>
              <CategoryPicker
                categories={categories}
                // A legacy value the catalog does not know stays as it is until another is picked.
                selectedId={selectedCategory?.id ?? (hasUnknownCategory ? null : '')}
                buttonLabel={
                  selectedCategory
                    ? selectedCategory.name
                    : hasUnknownCategory
                      ? `${categoryRef.name.trim() || 'удалённая категория'} (нет в справочнике)`
                      : '— не задана —'
                }
                onSelect={(id) => {
                  const next = categories.find((item) => item.id === id)
                  setCategoryRef(next ? { id: next.id, name: next.name } : { id: '', name: '' })
                }}
                labelledBy="material-category-label"
              />
            </div>

            <label className="grid gap-1.5 text-sm">
              <span className="font-medium">Единица измерения</span>
              <Select
                value={unitId}
                onChange={(event) => {
                  const next = units.find((item) => item.id === event.target.value)
                  setUnitId(event.target.value)
                  setUnitSymbol(next?.symbol ?? '')
                }}
              >
                <option value="">— не задана —</option>
                {units.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name} ({item.symbol})
                  </option>
                ))}
              </Select>
              {unitId === '' && unitSymbol !== '' ? (
                <span className="text-muted-foreground text-xs">
                  В документе сохранён символ «{unitSymbol}» без ссылки на справочник.
                </span>
              ) : null}
            </label>

            <label className="grid gap-1.5 text-sm">
              <span className="font-medium">Тип затрат</span>
              <Select
                value={costType}
                onChange={(event) => setCostType(event.target.value as CostType)}
              >
                {COST_TYPES.map((value) => (
                  <option key={value} value={value}>
                    {COST_TYPE_LABELS[value]}
                  </option>
                ))}
              </Select>
            </label>

            <label className="grid gap-1.5 text-sm">
              <span className="font-medium">Видимость</span>
              <Select
                value={isPrivate ? 'private' : 'shared'}
                onChange={(event) => setPrivate(event.target.value === 'private')}
              >
                <option value="shared">Общий каталог</option>
                <option value="private">Личный пункт</option>
              </Select>
            </label>

            <label className="grid gap-1.5 text-sm">
              <span className="font-medium">Цена</span>
              <div className="flex gap-1.5">
                <Input
                  value={priceAmount}
                  onChange={(event) => setPriceAmount(event.target.value)}
                  inputMode="decimal"
                  placeholder="не задана"
                />
                <Select
                  value={currency}
                  onChange={(event) => setCurrency(event.target.value as Currency)}
                  className="w-20"
                  aria-label="Валюта цены"
                >
                  {CURRENCIES.map((value) => (
                    <option key={value} value={value}>
                      {CURRENCY_SYMBOLS[value]}
                    </option>
                  ))}
                </Select>
              </div>
              <span className="text-muted-foreground text-xs">
                {storedPrice
                  ? `Обновил: ${storedPrice.updatedByName || '—'}. Пустое поле убирает цену.`
                  : 'Цена общая для общего пункта и личная для личного. Пустое поле — цены нет.'}
              </span>
            </label>

            <label className="grid gap-1.5 text-sm">
              <span className="font-medium">Состояние</span>
              <Select
                value={isActive ? 'active' : 'inactive'}
                onChange={(event) => setActive(event.target.value === 'active')}
              >
                <option value="active">Активен</option>
                <option value="inactive">Скрыт</option>
              </Select>
            </label>
          </div>

          <label className="grid gap-1.5 text-sm">
            <span className="font-medium">Описание (ru)</span>
            <Textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={3}
            />
          </label>

          <div className="grid gap-3 rounded-lg border p-3">
            <span className="flex items-center gap-2 text-sm font-medium">
              <Globe className="size-4" />
              Переводы
            </span>
            {TRANSLATION_LANGUAGES.map((language) => (
              <div key={language.tag} className="grid gap-2 sm:grid-cols-2">
                <TranslatedInput
                  value={nameI18n[language.tag] ?? ''}
                  onChange={(event) =>
                    setNameI18n({ ...nameI18n, [language.tag]: event.target.value })
                  }
                  source={name}
                  lang={language.tag}
                  onTranslated={(text) =>
                    setNameI18n((prev) => ({ ...prev, [language.tag]: text }))
                  }
                  placeholder={`Название · ${language.label}`}
                  aria-label={`Название (${language.label})`}
                />
                <TranslatedInput
                  value={descriptionI18n[language.tag] ?? ''}
                  onChange={(event) =>
                    setDescriptionI18n({ ...descriptionI18n, [language.tag]: event.target.value })
                  }
                  source={description}
                  lang={language.tag}
                  onTranslated={(text) =>
                    setDescriptionI18n((prev) => ({ ...prev, [language.tag]: text }))
                  }
                  placeholder={`Описание · ${language.label}`}
                  aria-label={`Описание (${language.label})`}
                />
              </div>
            ))}
          </div>

          <div className="grid gap-3 rounded-lg border p-3">
            <span className="flex items-center gap-2 text-sm font-medium">
              <Sliders className="size-4" />
              Атрибуты категории
            </span>
            {selectedCategory === null ? (
              <p className="text-muted-foreground text-xs">
                Выберите категорию из справочника — её атрибуты появятся здесь.
              </p>
            ) : attributes.length === 0 ? (
              <p className="text-muted-foreground text-xs">
                У категории «{selectedCategory.name}» нет атрибутов. Их можно добавить в разделе
                «Категории».
              </p>
            ) : (
              <>
                {attributes.map((attribute) => (
                  <label key={attribute.id} className="grid gap-1.5 text-sm">
                    <span>
                      {attribute.name}
                      <span className="text-muted-foreground">
                        {attribute.code ? ` · ${attribute.code}` : ''}
                      </span>
                    </span>
                    <Input
                      value={attrValues[attribute.id] ?? ''}
                      onChange={(event) =>
                        setAttrValues({ ...attrValues, [attribute.id]: event.target.value })
                      }
                      inputMode={attribute.type === 'NUMBER' ? 'decimal' : 'text'}
                      placeholder={attribute.type === 'NUMBER' ? 'число' : 'текст'}
                    />
                  </label>
                ))}
                <p className="text-muted-foreground text-xs">
                  Пустые значения не сохраняются. Смена категории очищает значения предыдущей — так же
                  ведёт себя мобильное приложение.
                </p>
              </>
            )}
          </div>

          {material ? (
            <p className="text-muted-foreground text-xs">
              Создал: {material.createdByName || '—'} · последнее изменение:{' '}
              {material.updatedByName || '—'}
            </p>
          ) : null}

          {error ? (
            <Alert variant="destructive">
              <AlertTriangle />
              <AlertTitle>Не сохранено</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Отмена
          </Button>
          <Button onClick={() => void handleSave()} disabled={!canSave}>
            {isSaving ? <Loader2 className="animate-spin" /> : null}
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Drops blank overrides so the stored map only holds meaningful translations. */
function sanitize(values: Record<string, string>): Record<string, string> {
  const result: Record<string, string> = {}
  for (const [tag, value] of Object.entries(values)) {
    const trimmed = value.trim()
    if (trimmed.length > 0) result[tag] = trimmed
  }
  return result
}
